import { useTranslation } from 'react-i18next';
import { RadioGroupItem } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { displayValue, formatBrand } from '@subscriptions/presentation/utils';

type SavedCardRadioItemProps = {
  card: PaymentMethodViewDto;
  isSelected: boolean;
};

/**
 * Saved-card option rendered inside the saved-cards step `RadioGroup`.
 *
 * Presentation-only: selection state is driven by the parent `RadioGroup` value.
 * Shows `{Brand} **** {last4}` (brand via `formatBrand`) with the `MM/YY`
 * expiration as the secondary line — no holder name (product override).
 *
 * @param props - Component properties
 * @returns React element rendering the card option
 */
export const SavedCardRadioItem = ({ card, isSelected }: SavedCardRadioItemProps) => {
  const { t } = useTranslation('subscriptions');
  const inputId = `saved-card-${card.uuid}`;

  return (
    <label
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-3',
        'rounded-xl border bg-popover p-4 shadow-sm transition-colors',
        {
          'border-2 border-primary': isSelected,
          'border-border hover:border-gray-300': !isSelected,
        },
      )}
      htmlFor={inputId}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-base font-medium leading-6 text-foreground">
          {t('paymentMethod.cardLabel', {
            brand: formatBrand(card.brand),
            lastFourDigits: displayValue(card.lastFourDigits),
          })}
        </p>
        <p className="text-sm leading-5 text-muted-foreground">{displayValue(card.expirationDate)}</p>
      </div>
      <RadioGroupItem className="h-4 w-4 shrink-0" id={inputId} value={card.uuid} />
    </label>
  );
};
